import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Container,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Image,
  useToast,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { Navigate, useNavigate } from "react-router-dom";
import { InputText, InputPassword } from "../../components/Input";
import { useSSO } from "../../context/SSO";
import { loginUser } from "../../services/authService";

const schema = yup.object().shape({
  email: yup
    .string()
    .required("Email é obrigatório")
    .email("Digite um email válido")
    .max(255, "Email muito longo"),
  password: yup
    .string()
    .required("Senha é obrigatória")
    .min(1, "Senha é obrigatória"),
});

export default function Login() {
  const { authenticated, login, isAuthLoading } = useSSO();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Handle language parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const language = params.get("lan");
    if (language) {
      localStorage.setItem("i18nextLng", language);
    }
  }, []);

  // Se ainda está carregando a autenticação, mostra spinner
  if (isAuthLoading) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Verificando autenticação...</Text>
        </VStack>
      </Center>
    );
  }

  if (authenticated) return <Navigate to="/" replace />;

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError("");
      const loginData = await loginUser(data.email, data.password);
      login(loginData);

      // Verifica se é primeiro acesso
      if (loginData.first_access) {
        toast({
          title: "Primeiro Acesso",
          description: "Por favor, defina uma nova senha para continuar",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        navigate("/reset-password", { replace: true });
        return;
      }

      toast({
        title: "Login realizado com sucesso!",
        description: `Bem-vindo!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      reset();

      // Redirecionar baseado no role do usuário
      const userRoles = loginData.user?.roles || [];
      const hasMasterRole = userRoles.some((role) => role.name === "MASTER");
      const hasAdminCompanyRole = userRoles.some(
        (role) => role.name === "RESPONSAVEL"
      );

      if (hasMasterRole) {
        navigate("/registration");
      } else if (hasAdminCompanyRole) {
        navigate("/survey-analysis");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(
        err.message || "Erro ao fazer login. Tente novamente."
      );

      toast({
        title: "Erro no login",
        description:
          err.response?.data?.message ||
          "Verifique suas credenciais e tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={12}>
      <Container maxW="md">
        <VStack spacing={8}>
          {/* Logo e Título */}
          <Box textAlign="center">
            <Image
              src="/logo.svg"
              alt="Logo GS Survey"
              maxH="80px"
              mx="auto"
              mb={0}
            />
          </Box>

          {/* Formulário de Login */}
          <Box
            w="100%"
            bg="white"
            p={8}
            borderRadius="xl"
            boxShadow="lg"
            border="1px"
            borderColor="gray.200"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={6}>
                {error && (
                  <Alert status="error" borderRadius="md" bg="red.50" borderColor="red.200" borderWidth="1px">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <InputText
                  {...register("email")}
                  name="email"
                  label="Email"
                  placeholder="Digite seu email"
                  error={errors.email}
                  type="email"
                  autoComplete="email"
                  isDisabled={isLoading}
                />

                <InputPassword
                  {...register("password")}
                  name="password"
                  label="Senha"
                  placeholder="Digite sua senha"
                  error={errors.password}
                  autoComplete="current-password"
                  isDisabled={isLoading}
                />

                <Button
                  type="submit"
                  w="100%"
                  size="lg"
                  colorScheme="blue"
                  isLoading={isLoading}
                  loadingText="Entrando..."
                  isDisabled={isLoading}
                  _hover={{ bg: "blue.600" }}
                  _active={{ bg: "blue.700" }}
                >
                  Entrar
                </Button>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
